import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

/**
 * Phone approvals (backend docs/PHONE_APPROVALS.md): when the company's
 * security settings require it, a protected request answers 428
 * {code: "approval_required", challengeId, expiresAt, summary}. The
 * interceptor shows "Approve on your phone" (ApprovalModal), waits for the
 * phone's decision, and sends the same request again with ?approvalId=,
 * which the backend accepts once for that exact request.
 */
export type ApprovalState = {
  challengeId: string;
  expiresAt: number;
  summary: string;
  status: "waiting" | "denied" | "expired";
  message?: string;
};

type Listener = (s: ApprovalState | null) => void;
type RetriedConfig = InternalAxiosRequestConfig & { _approvalRetry?: boolean };

const POLL_MS = 2000;
let current: ApprovalState | null = null;
let cancelCurrent: (() => void) | null = null;
const listeners = new Set<Listener>();

function emit(s: ApprovalState | null) {
  current = s;
  listeners.forEach((l) => l(s));
}

export function subscribeApproval(l: Listener): () => void {
  listeners.add(l);
  l(current);
  return () => {
    listeners.delete(l);
  };
}

/** Stop waiting (the user closed the dialog); the request fails. */
export function cancelApproval() {
  if (cancelCurrent) cancelCurrent();
  else emit(null);
}

function waitForPhone(apiUrl: string, authorization: string | undefined, req: ApprovalState): Promise<boolean> {
  emit({ ...req, status: "waiting" });
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean, status?: ApprovalState["status"], message?: string) => {
      if (done) return;
      done = true;
      clearInterval(timer);
      cancelCurrent = null;
      emit(ok || !status ? null : { ...req, status, message });
      resolve(ok);
    };
    cancelCurrent = () => finish(false);
    const timer = setInterval(async () => {
      if (Date.now() >= req.expiresAt) {
        finish(false, "expired", "The approval request expired. Try again.");
        return;
      }
      try {
        const r = await axios.get(`${apiUrl}/approvals/v1/challenges/${encodeURIComponent(req.challengeId)}`, {
          headers: authorization ? { Authorization: authorization } : {},
        });
        const status = r.data?.challenge?.status;
        if (status === "approved") finish(true);
        else if (status === "denied") finish(false, "denied", "Declined on your phone.");
        else if (status === "expired") finish(false, "expired", "The approval request expired. Try again.");
      } catch (_) {
        // keep waiting: a network blip shouldn't end it
      }
    }, POLL_MS);
  });
}

/** Approve-and-retry on an axios instance. apiUrl: the API's base URL. */
export function installApprovalInterceptor(instance: AxiosInstance, apiUrl: string) {
  instance.interceptors.response.use(undefined, async (error: AxiosError<any>) => {
    const res = error.response;
    const cfg = error.config as RetriedConfig | undefined;
    if (res?.status !== 428 || res.data?.code !== "approval_required" || !cfg || cfg._approvalRetry) {
      throw error;
    }
    const authorization = (cfg.headers?.Authorization ?? cfg.headers?.authorization) as string | undefined;
    const approved = await waitForPhone(apiUrl, authorization, {
      challengeId: res.data.challengeId,
      expiresAt: res.data.expiresAt,
      status: "waiting",
      summary: res.data.summary,
    });
    if (!approved) {
      res.data = { ...res.data, code: "approval_not_given", message: "Not approved on your phone." };
      throw error;
    }
    cfg._approvalRetry = true;
    cfg.params = { ...(cfg.params ?? {}), approvalId: res.data.challengeId };
    return instance.request(cfg);
  });
}
